import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  RegisterPageProps,
  useLink,
  useRouterType,
  useRouterContext,
  useNavigation,
} from "@refinedev/core";
import { Form, Input, Typography, message } from "antd";
import MaskedInput from "antd-mask-input";
import {
  CheckOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
  LoadingOutlined,
  SearchOutlined,
  LockOutlined,
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  BankOutlined,
  PercentageOutlined,
  SafetyCertificateOutlined,
  CheckCircleFilled,
} from "@ant-design/icons";
import axios from "axios";
import { API_URL, PAGARME_PIX_FEE, PAGARME_BOLETO_FEE, PAGARME_TRANSFER_FEE, TURNSTILE_SITE_KEY } from "../../../constants";
import { TurnstileWidget } from "components";
import styles from "./register.module.css";

const { Title, Text } = Typography;

type Step = 1 | 2 | 3 | 4 | 5;

interface FormData {
  name: string;
  email: string;
  phone: string;
  countryCode: string;
  password: string;
  cpfCnpj: string;
  razaoSocial: string;
  nomeFantasia: string;
  termsAccepted: boolean;
}

const STEPS: { id: Step; label: string }[] = [
  { id: 1, label: "Conta" },
  { id: 2, label: "Empresa" },
  { id: 3, label: "Taxas" },
  { id: 4, label: "Termos" },
  { id: 5, label: "Confirmar" },
];

const STRONG_PASSWORD = /(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{8,})/;

export const RegisterPage: React.FC<RegisterPageProps<any, any, any>> = () => {
  const routerType = useRouterType();
  const Link = useLink();
  const { Link: LegacyLink } = useRouterContext();
  const ActiveLink = routerType === "legacy" ? LegacyLink : Link;
  const { push } = useNavigation();

  const [step, setStep] = useState<Step>(1);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [cnpjLoading, setCnpjLoading] = useState(false);
  const [cnpjFound, setCnpjFound] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaKey, setCaptchaKey] = useState(0);

  const [data, setData] = useState<FormData>({
    name: "",
    email: "",
    phone: "",
    countryCode: "BR",
    password: "",
    cpfCnpj: "",
    razaoSocial: "",
    nomeFantasia: "",
    termsAccepted: false,
  });

  const update = (patch: Partial<FormData>) => setData((d) => ({ ...d, ...patch }));

  // ===== Validação por step =====
  const validateStep = (s: Step): string | null => {
    if (s === 1) {
      if (!data.name || data.name.trim().length < 2) return "Informe seu nome.";
      if (!data.email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(data.email)) return "E-mail inválido.";
      if (!data.phone || data.phone.replace(/\D/g, "").length < 10) return "Telefone inválido.";
      if (!STRONG_PASSWORD.test(data.password))
        return "A senha precisa ter no mínimo 8 caracteres, com uma letra maiúscula, uma minúscula e um número.";
    }
    if (s === 2) {
      const cleanCnpj = data.cpfCnpj.replace(/\D/g, "");
      if (cleanCnpj.length !== 14) return "Informe um CNPJ válido (14 dígitos).";
      if (!data.razaoSocial || data.razaoSocial.trim().length < 2)
        return "Informe a razão social da empresa.";
    }
    if (s === 4) {
      if (!data.termsAccepted) {
        return "É necessário aceitar os Termos de Uso e a Política de Privacidade.";
      }
      if (!captchaToken) {
        return "Confirme a verificação anti-bot abaixo antes de criar a conta.";
      }
    }
    return null;
  };

  useEffect(() => {
    if (step === 4) {
      setCaptchaToken("");
      setCaptchaKey((k) => k + 1);
    }
  }, [step]);

  const next = () => {
    const err = validateStep(step);
    if (err) {
      setErrorMsg(err);
      return;
    }
    setErrorMsg("");
    if (step === 4) {
      submitSignup();
    } else {
      setStep((s) => (s + 1) as Step);
    }
  };

  const back = () => {
    setErrorMsg("");
    if (step > 1) setStep((s) => (s - 1) as Step);
  };

  // ===== Lookup CNPJ (BrasilAPI via backend) =====
  const lookupCnpj = async () => {
    const cleanCnpj = data.cpfCnpj.replace(/\D/g, "");
    if (cleanCnpj.length !== 14) {
      setErrorMsg("Digite o CNPJ completo antes de buscar.");
      return;
    }
    setCnpjLoading(true);
    setErrorMsg("");
    try {
      const { data: r } = await axios.get(`${API_URL}/signup/cnpj/${cleanCnpj}`);
      update({
        razaoSocial: r.razaoSocial || data.razaoSocial,
        nomeFantasia: r.nomeFantasia || r.razaoSocial || data.nomeFantasia,
      });
      setCnpjFound(r.razaoSocial || "Empresa encontrada");
    } catch (err: any) {
      const m = err?.response?.data?.message || "Não foi possível consultar o CNPJ.";
      setErrorMsg(m);
      setCnpjFound(null);
    } finally {
      setCnpjLoading(false);
    }
  };

  // ===== Submit final =====
  const submitSignup = async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      await axios.post(`${API_URL}/auth/signup`, {
        name: data.name.trim(),
        email: data.email.trim().toLowerCase(),
        phone: data.phone.trim(),
        countryCode: data.countryCode || "BR",
        password: data.password,
        cpfCnpj: data.cpfCnpj.replace(/\D/g, ""),
        razaoSocial: data.razaoSocial.trim(),
        termsAccepted: true,
        captchaToken,
        source: "wizard",
      });
      setStep(5);
    } catch (err: any) {
      const m = err?.response?.data?.message || "Não foi possível concluir o cadastro. Verifique e tente novamente.";
      setErrorMsg(m);
      setCaptchaToken("");
      setCaptchaKey((k) => k + 1);
    } finally {
      setLoading(false);
    }
  };

  // ===== Verificação de código =====
  const [code, setCode] = useState<string[]>(["", "", "", "", "", ""]);
  const codeRefs = useRef<Array<HTMLInputElement | null>>([]);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (step === 5) {
      setResendCooldown(60);
      const t = setInterval(() => {
        setResendCooldown((c) => (c > 0 ? c - 1 : 0));
      }, 1000);
      setTimeout(() => codeRefs.current[0]?.focus(), 200);
      return () => clearInterval(t);
    }
  }, [step]);

  const handleCodeChange = (idx: number, val: string) => {
    const digit = val.replace(/\D/g, "").slice(-1);
    const next = [...code];
    next[idx] = digit;
    setCode(next);
    if (digit && idx < 5) codeRefs.current[idx + 1]?.focus();
  };

  const handleCodeKey = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !code[idx] && idx > 0) {
      codeRefs.current[idx - 1]?.focus();
    }
    if (e.key === "ArrowLeft" && idx > 0) codeRefs.current[idx - 1]?.focus();
    if (e.key === "ArrowRight" && idx < 5) codeRefs.current[idx + 1]?.focus();
  };

  const handleCodePaste = (e: React.ClipboardEvent) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted) {
      const arr = pasted.split("");
      const next = ["", "", "", "", "", ""];
      arr.forEach((d, i) => (next[i] = d));
      setCode(next);
      e.preventDefault();
      const nextEmpty = Math.min(arr.length, 5);
      codeRefs.current[nextEmpty]?.focus();
    }
  };

  const verifyCode = async () => {
    const c = code.join("");
    if (c.length !== 6) {
      setErrorMsg("Digite os 6 dígitos do código.");
      return;
    }
    setVerifyLoading(true);
    setErrorMsg("");
    try {
      await axios.post(`${API_URL}/auth/verify-email`, {
        email: data.email.trim().toLowerCase(),
        code: c,
      });
      message.success("E-mail confirmado! Você já pode fazer login.");
      setTimeout(() => push("/login"), 1500);
    } catch (err: any) {
      const m = err?.response?.data?.message || "Código inválido ou expirado.";
      setErrorMsg(m);
    } finally {
      setVerifyLoading(false);
    }
  };

  const resendCode = async () => {
    if (resendCooldown > 0) return;
    setErrorMsg("");
    try {
      await axios.post(`${API_URL}/auth/resend-verification`, {
        email: data.email.trim().toLowerCase(),
      });
      message.success("Novo código enviado para seu e-mail.");
      setResendCooldown(60);
    } catch {
      message.error("Não foi possível reenviar agora. Tente em alguns instantes.");
    }
  };

  // ===== UI =====
  const stepperUI = useMemo(() => {
    if (step === 5) return null; // tela de confirmação tem seu próprio header
    return (
      <div className={styles.stepper}>
        {STEPS.slice(0, 4).map((s, i) => (
          <React.Fragment key={s.id}>
            <div className={styles.stepItem}>
              <span
                className={[
                  styles.stepBullet,
                  step === s.id ? styles.active : "",
                  step > s.id ? styles.done : "",
                ].join(" ")}
              >
                {step > s.id ? <CheckOutlined /> : s.id}
              </span>
              <span className={[styles.stepLabel, step === s.id ? styles.active : ""].join(" ")}>
                {s.label}
              </span>
            </div>
            {i < 3 && (
              <div className={[styles.stepBar, step > s.id ? styles.done : ""].join(" ")} />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  }, [step]);

  return (
    <div className={styles.wrapper}>
      {/* Painel esquerda */}
      <div className={styles.brandPanel}>
        <div className={styles.brandGlow} />
        <div className={styles.brandContent}>
          <img src="/images/logo.png" alt="Split Fácil" className={styles.brandLogo} />
          <h1 className={styles.brandHeadline}>
            Comece a dividir pagamentos automaticamente em minutos.
          </h1>
          <p className={styles.brandText}>
            Crie cobranças, configure recebedores e deixe o Split Fácil distribuir o dinheiro
            automaticamente entre todos os envolvidos.
          </p>
          <ul className={styles.brandFeatures}>
            <li>
              <span className={styles.brandCheck}><CheckOutlined /></span>
              Split automático integrado ao Pagar.me
            </li>
            <li>
              <span className={styles.brandCheck}><CheckOutlined /></span>
              PIX, boleto e cartão em uma só plataforma
            </li>
            <li>
              <span className={styles.brandCheck}><CheckOutlined /></span>
              Sem mensalidade, sem fidelidade
            </li>
            <li>
              <span className={styles.brandCheck}><CheckOutlined /></span>
              LGPD e dados criptografados
            </li>
          </ul>
        </div>
      </div>

      {/* Painel direita */}
      <div className={styles.formPanel}>
        <div className={styles.fadeUp}>
          <div className={styles.logoMobile}>
            <img src="/images/logo.png" alt="Split Fácil" />
          </div>

          {step !== 5 && (
            <div className={styles.cardHeader}>
              <Title className={styles.cardTitle}>Crie sua conta</Title>
              <Text className={styles.cardSubtitle}>
                {step === 1 && "Comece informando seus dados pessoais."}
                {step === 2 && "Agora os dados da sua empresa."}
                {step === 3 && "Veja como funcionam as taxas."}
                {step === 4 && "Última etapa: aceite dos termos."}
              </Text>
            </div>
          )}

          {stepperUI}

          {errorMsg && <div className={styles.error}>{errorMsg}</div>}

          {/* === Step 1: Dados pessoais === */}
          {step === 1 && (
            <Form layout="vertical" requiredMark={false}>
              <Form.Item label="Nome completo" required>
                <Input
                  size="large"
                  prefix={<UserOutlined style={{ color: "#94a3b8" }} />}
                  placeholder="Seu nome"
                  value={data.name}
                  onChange={(e) => update({ name: e.target.value })}
                />
              </Form.Item>
              <Form.Item label="E-mail" required tooltip="Será o login da conta master da empresa.">
                <Input
                  size="large"
                  prefix={<MailOutlined style={{ color: "#94a3b8" }} />}
                  placeholder="voce@empresa.com.br"
                  type="email"
                  value={data.email}
                  onChange={(e) => update({ email: e.target.value })}
                  autoComplete="email"
                />
              </Form.Item>
              <Form.Item label="Telefone" required>
                <MaskedInput
                  mask="(00) 00000-0000"
                  size="large"
                  prefix={<PhoneOutlined style={{ color: "#94a3b8" }} />}
                  placeholder="(11) 99999-9999"
                  value={data.phone}
                  onChange={(e: any) => update({ phone: e.target.value })}
                />
              </Form.Item>
              <Form.Item
                label="Senha"
                required
                tooltip="Mínimo 8 caracteres com letra maiúscula, minúscula e número."
              >
                <Input.Password
                  size="large"
                  prefix={<LockOutlined style={{ color: "#94a3b8" }} />}
                  placeholder="●●●●●●●●"
                  value={data.password}
                  onChange={(e) => update({ password: e.target.value })}
                  autoComplete="new-password"
                />
              </Form.Item>
            </Form>
          )}

          {/* === Step 2: Empresa === */}
          {step === 2 && (
            <Form layout="vertical" requiredMark={false}>
              <Form.Item label="CNPJ" required tooltip="Você pode buscar os dados automaticamente após digitar.">
                <MaskedInput
                  mask="00.000.000/0000-00"
                  size="large"
                  prefix={<BankOutlined style={{ color: "#94a3b8" }} />}
                  placeholder="00.000.000/0000-00"
                  value={data.cpfCnpj}
                  onChange={(e: any) => {
                    update({ cpfCnpj: e.target.value });
                    setCnpjFound(null);
                  }}
                />
                <button
                  type="button"
                  className={styles.cnpjLookupBtn}
                  onClick={lookupCnpj}
                  disabled={cnpjLoading || data.cpfCnpj.replace(/\D/g, "").length !== 14}
                >
                  {cnpjLoading ? <LoadingOutlined /> : <SearchOutlined />}
                  {cnpjLoading ? "Buscando..." : "Preencher automaticamente"}
                </button>
              </Form.Item>

              {cnpjFound && (
                <div className={styles.cnpjFound}>
                  <CheckCircleFilled /> Empresa encontrada: <strong>{cnpjFound}</strong>
                </div>
              )}

              <Form.Item label="Razão social" required>
                <Input
                  size="large"
                  placeholder="Sua Empresa LTDA"
                  value={data.razaoSocial}
                  onChange={(e) => update({ razaoSocial: e.target.value })}
                />
              </Form.Item>
              <Form.Item label="Nome fantasia (opcional)">
                <Input
                  size="large"
                  placeholder="Como sua empresa é conhecida"
                  value={data.nomeFantasia}
                  onChange={(e) => update({ nomeFantasia: e.target.value })}
                />
              </Form.Item>
            </Form>
          )}

          {/* === Step 3: Taxas === */}
          {step === 3 && (
            <div>
              <div className={styles.feesCard}>
                <div className={styles.feesTitle}>
                  <PercentageOutlined style={{ color: "#2563eb" }} /> Taxas por transação
                </div>
                <p className={styles.feesSubtitle}>
                  Pagamento das taxas do gateway Pagar.me, descontadas automaticamente em cada
                  cobrança paga.
                </p>
                <ul className={styles.feesList}>
                  <li>
                    <strong>PIX</strong>
                    <span>R$ {PAGARME_PIX_FEE.toFixed(2).replace(".", ",")}</span>
                  </li>
                  <li>
                    <strong>Boleto</strong>
                    <span>R$ {PAGARME_BOLETO_FEE.toFixed(2).replace(".", ",")}</span>
                  </li>
                  <li>
                    <strong>Transferência</strong>
                    <span>R$ {PAGARME_TRANSFER_FEE.toFixed(2).replace(".", ",")}</span>
                  </li>
                  <li>
                    <strong>Mensalidade</strong>
                    <span>Sem cobrança</span>
                  </li>
                </ul>
                <div className={styles.feesHighlight}>
                  <strong>Taxa de serviço Split Fácil: 2%</strong> sobre o valor de cada cobrança
                  paga. É deduzida automaticamente no momento do pagamento - você não precisa
                  fazer nada.
                </div>
              </div>

              <div className={styles.feesCard}>
                <div className={styles.feesTitle}>
                  <SafetyCertificateOutlined style={{ color: "#10b981" }} /> Como funciona o split
                </div>
                <p className={styles.feesSubtitle}>
                  Numa cobrança de <strong>R$ 1.000,00</strong> dividida entre 3 recebedores:
                </p>
                <ul className={styles.feesList} style={{ gridTemplateColumns: "1fr" }}>
                  <li>
                    <strong>Recebedor A (40%)</strong>
                    <span style={{ color: "#10b981" }}>R$ 400,00</span>
                  </li>
                  <li>
                    <strong>Recebedor B (35%)</strong>
                    <span style={{ color: "#10b981" }}>R$ 350,00</span>
                  </li>
                  <li>
                    <strong>Recebedor C (25%)</strong>
                    <span style={{ color: "#10b981" }}>R$ 250,00</span>
                  </li>
                </ul>
                <div className={styles.feesHighlight}>
                  Cada recebedor recebe direto na sua conta bancária. Você define as porcentagens
                  ou valores fixos por cobrança.
                </div>
              </div>
            </div>
          )}

          {/* === Step 4: Termos === */}
          {step === 4 && (
            <div>
              <div className={styles.termsBox}>
                Antes de finalizar, por favor leia e aceite os documentos abaixo. Eles explicam
                como usamos seus dados e quais são os seus direitos como cliente.
                <br /><br />
                <a href={`${API_URL}/legal/terms`} target="_blank" rel="noreferrer">
                  📄 Termos de Uso
                </a>
                {" • "}
                <a href={`${API_URL}/legal/privacy`} target="_blank" rel="noreferrer">
                  🔒 Política de Privacidade
                </a>
              </div>

              <label
                style={{
                  display: "flex",
                  gap: "0.75rem",
                  alignItems: "flex-start",
                  padding: "1rem 1.1rem",
                  border: "2px solid",
                  borderColor: data.termsAccepted ? "#2563eb" : "#e2e8f0",
                  borderRadius: "0.85rem",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  background: data.termsAccepted ? "rgba(37, 99, 235, 0.04)" : "#fff",
                }}
              >
                <input
                  type="checkbox"
                  checked={data.termsAccepted}
                  onChange={(e) => update({ termsAccepted: e.target.checked })}
                  style={{
                    width: 20,
                    height: 20,
                    accentColor: "#2563eb",
                    marginTop: 2,
                    cursor: "pointer",
                  }}
                />
                <span style={{ fontSize: "0.92rem", color: "#475569", lineHeight: 1.5 }}>
                  <strong>Li e aceito</strong> os Termos de Uso e a Política de Privacidade do
                  Split Fácil. Estou ciente das taxas aplicáveis e autorizo o tratamento dos meus
                  dados conforme descrito.
                </span>
              </label>

              <div className={styles.turnstileBox}>
                <p className={styles.turnstileLabel}>Verificação de segurança</p>
                <TurnstileWidget
                  key={captchaKey}
                  siteKey={TURNSTILE_SITE_KEY}
                  onSuccess={setCaptchaToken}
                  onExpire={() => setCaptchaToken("")}
                />
                {!captchaToken && (
                  <p className={styles.turnstileHint}>Marque a caixa acima para continuar.</p>
                )}
              </div>
            </div>
          )}

          {/* === Step 5: Verificação === */}
          {step === 5 && (
            <div className={styles.finalCard}>
              <div className={styles.finalIcon}>
                <MailOutlined />
              </div>
              <Title className={styles.finalTitle}>Verifique seu e-mail</Title>
              <Text className={styles.finalText}>
                Enviamos um código de 6 dígitos para <strong>{data.email}</strong>.
                Digite o código abaixo para ativar sua conta.
              </Text>

              <div className={styles.codeBox} onPaste={handleCodePaste}>
                {code.map((c, i) => (
                  <input
                    key={i}
                    ref={(el) => {
                      codeRefs.current[i] = el;
                    }}
                    className={[styles.codeInput, c ? styles.filled : ""].join(" ")}
                    value={c}
                    inputMode="numeric"
                    pattern="\d*"
                    maxLength={1}
                    onChange={(e) => handleCodeChange(i, e.target.value)}
                    onKeyDown={(e) => handleCodeKey(i, e)}
                    autoFocus={i === 0}
                  />
                ))}
              </div>

              <p className={styles.codeHelp}>
                Não recebeu? Confira sua caixa de spam ou{" "}
              </p>
              <p className={styles.codeResend}>
                <button onClick={resendCode} disabled={resendCooldown > 0}>
                  {resendCooldown > 0 ? `Reenviar em ${resendCooldown}s` : "Reenviar código"}
                </button>
              </p>

              <button
                className={styles.primaryButton}
                onClick={verifyCode}
                disabled={verifyLoading || code.join("").length !== 6}
                style={{ width: "100%", marginTop: "1.25rem" }}
              >
                {verifyLoading ? <LoadingOutlined /> : <CheckOutlined />}
                {verifyLoading ? "Verificando..." : "Confirmar e ativar conta"}
              </button>

              <p className={styles.loginLink}>
                <ActiveLink to="/login">Voltar para o login</ActiveLink>
              </p>
            </div>
          )}

          {/* === Footer de ações === */}
          {step !== 5 && (
            <>
              <div className={styles.actions}>
                {step > 1 && (
                  <button className={styles.secondaryButton} onClick={back} disabled={loading}>
                    <ArrowLeftOutlined style={{ marginRight: 6 }} /> Voltar
                  </button>
                )}
                <button
                  className={styles.primaryButton}
                  onClick={next}
                  disabled={loading || (step === 4 && !captchaToken)}
                >
                  {loading ? (
                    <>
                      <LoadingOutlined style={{ marginRight: 8 }} /> Enviando...
                    </>
                  ) : step === 4 ? (
                    <>
                      Criar conta <CheckOutlined style={{ marginLeft: 8 }} />
                    </>
                  ) : (
                    <>
                      Continuar <ArrowRightOutlined style={{ marginLeft: 8 }} />
                    </>
                  )}
                </button>
              </div>

              <p className={styles.loginLink}>
                Já tem uma conta?
                <ActiveLink to="/login">Fazer login</ActiveLink>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
